import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, KeyRound, UserX, GitBranch, CheckCircle2, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
const hdr = () => { const t = localStorage.getItem('token'); return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };
const api = {
  get: (p) => fetch(`${API_URL}/directory${p}`, { headers: hdr() }).then(r => r.json()),
  post: (p, b) => fetch(`${API_URL}/directory${p}`, { method: 'POST', headers: hdr(), body: JSON.stringify(b || {}) }),
};
const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z]/g, '');

export default function Directorio() {
  const { user } = useAuth();
  const roles = [user?.role, ...(user?.extra_roles || [])];
  const isApprover = roles.some(r => ['admin', 'technician'].includes(r));
  const [tab, setTab] = useState('alta');
  const [domains, setDomains] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [msg, setMsg] = useState(null);
  const flash = (m, ok = true) => { setMsg({ m, ok }); setTimeout(() => setMsg(null), 4500); };

  useEffect(() => {
    api.get('/domains').then(d => setDomains(Array.isArray(d) ? d : [])).catch(() => {});
    api.get('/licenses').then(l => setLicenses(Array.isArray(l) ? l : [])).catch(() => {});
  }, []);

  const S = styles;
  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <h1 style={S.title}><UserPlus size={22} style={{ verticalAlign: -4 }} /> Directorio — Altas, bajas y contraseñas</h1>
        <div style={S.sub}>Provisioning de usuarios (Microsoft 365). Las contraseñas se entregan por link de un solo uso.</div>
      </div>
      <div style={S.tabs}>
        <button onClick={() => setTab('alta')} style={{ ...S.tab, ...(tab === 'alta' ? S.tabOn : {}) }}><UserPlus size={15} style={{ verticalAlign: -3 }} /> Alta</button>
        <button onClick={() => setTab('acciones')} style={{ ...S.tab, ...(tab === 'acciones' ? S.tabOn : {}) }}><KeyRound size={15} style={{ verticalAlign: -3 }} /> Reset / Baja</button>
        {isApprover && <button onClick={() => setTab('solicitudes')} style={{ ...S.tab, ...(tab === 'solicitudes' ? S.tabOn : {}) }}><GitBranch size={15} style={{ verticalAlign: -3 }} /> Solicitudes</button>}
      </div>
      {msg && <div style={{ ...S.flash, borderColor: msg.ok ? 'var(--green)' : 'var(--red)' }}>{msg.ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />} {msg.m}</div>}

      {tab === 'alta' && <AltaForm domains={domains} licenses={licenses} flash={flash} />}
      {tab === 'acciones' && <AccionesPanel flash={flash} />}
      {tab === 'solicitudes' && isApprover && <SolicitudesPanel flash={flash} />}
    </div>
  );
}

function AltaForm({ domains, licenses, flash }) {
  const S = styles;
  const [nom, setNom] = useState('');
  const [ape, setApe] = useState('');
  const [domain, setDomain] = useState('');
  const [choice, setChoice] = useState('');
  const [avail, setAvail] = useState({});
  const [checking, setChecking] = useState(false);
  const [sku, setSku] = useState('');
  const [usage, setUsage] = useState('AR');
  const [reqEmail, setReqEmail] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (domains.length && !domain) setDomain(domains[0].domain); }, [domains]); // eslint-disable-line

  const apParts = ape.trim().split(/\s+/).filter(Boolean);
  const ini = norm(nom).charAt(0);
  const variants = [];
  if (ini && apParts[0]) {
    variants.push(norm(apParts[0]) ? ini + norm(apParts[0]) : '');
    if (apParts[1]) variants.push(ini + norm(apParts[0]) + norm(apParts[1]));
  }
  const cleanVariants = [...new Set(variants.filter(Boolean))];

  const check = async () => {
    if (!domain || !cleanVariants.length) return;
    setChecking(true);
    const upns = cleanVariants.map(v => `${v}@${domain}`);
    try { const r = await api.post('/check-availability', { upns }); setAvail(await r.json()); }
    catch { flash('No se pudo verificar disponibilidad', false); }
    setChecking(false);
  };
  useEffect(() => { setAvail({}); setChoice(''); }, [nom, ape, domain]);

  const submit = async () => {
    if (!nom.trim() || !apParts.length) return flash('Completá nombre y apellido.', false);
    if (!choice) return flash('Elegí la variación de correo.', false);
    if (!domain) return flash('Elegí el dominio.', false);
    if (!reqEmail.trim()) return flash('El mail del solicitante es obligatorio.', false);
    setBusy(true);
    const upn = `${choice}@${domain}`;
    const display = `${nom.trim()} ${ape.trim()}`.replace(/\s+/g, ' ');
    const r = await api.post('/users', {
      display_name: display, mail_nickname: choice, user_principal_name: upn,
      usage_location: usage, sku_id: sku || null, requester_email: reqEmail || null,
    });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (r.ok && j.executed) flash(`Usuario ${upn} creado. Link de contraseña enviado.`);
    else if (r.status === 202 && j.pending) flash('Solicitud enviada para aprobación de un administrador.');
    else flash(j.error || 'Error creando el usuario.', false);
  };

  return (
    <div style={S.card}>
      <div style={S.row2}>
        <Field label="Nombre"><input value={nom} onChange={e => setNom(e.target.value)} style={S.input} placeholder="José" /></Field>
        <Field label="Apellido(s)"><input value={ape} onChange={e => setApe(e.target.value)} style={S.input} placeholder="Peña Gómez" /></Field>
      </div>
      <div style={S.row2}>
        <Field label="Dominio">
          <select value={domain} onChange={e => setDomain(e.target.value)} style={S.input}>
            {!domains.length && <option value="">(sin dominios)</option>}
            {domains.map(d => <option key={d.domain} value={d.domain}>{d.domain}{d.is_default ? ' (default)' : ''}</option>)}
          </select>
        </Field>
        <Field label="Licencia">
          <select value={sku} onChange={e => setSku(e.target.value)} style={S.input}>
            <option value="">Sin licencia</option>
            {licenses.map(l => <option key={l.sku_id} value={l.sku_id}>{l.name} ({l.available} libres)</option>)}
          </select>
        </Field>
      </div>

      <div style={{ margin: '4px 0 6px', fontSize: 13, color: 'var(--ink-2)' }}>Variación de correo</div>
      {!cleanVariants.length && <div style={S.dim}>Ingresá nombre y apellido para ver las opciones.</div>}
      <div style={{ display: 'grid', gap: 8 }}>
        {cleanVariants.map(v => {
          const upn = `${v}@${domain}`;
          const a = avail[upn];
          return (
            <label key={v} style={{ ...S.optRow, ...(choice === v ? { borderColor: 'var(--accent)', borderWidth: 2 } : {}), opacity: a === false ? .55 : 1 }}>
              <input type="radio" name="var" disabled={a === false} checked={choice === v} onChange={() => setChoice(v)} />
              <span style={{ fontFamily: 'monospace', fontSize: 14 }}>{upn}</span>
              <span style={{ marginLeft: 'auto' }}>
                {a === true && <span style={S.chipOk}>libre</span>}
                {a === false && <span style={S.chipNo}>ocupado</span>}
                {a === undefined && <span style={S.dim}>sin verificar</span>}
              </span>
            </label>
          );
        })}
      </div>
      {cleanVariants.length > 0 && <button style={{ ...S.btnGhost, marginTop: 8 }} onClick={check} disabled={checking}><RefreshCw size={13} style={{ verticalAlign: -2 }} /> {checking ? 'Verificando…' : 'Verificar disponibilidad'}</button>}

      <div style={{ ...S.row2, marginTop: 12 }}>
        <Field label="Ubicación de uso"><select value={usage} onChange={e => setUsage(e.target.value)} style={S.input}><option value="AR">Argentina (AR)</option><option value="CL">Chile (CL)</option><option value="UY">Uruguay (UY)</option><option value="MX">México (MX)</option></select></Field>
        <Field label="Mail del solicitante (obligatorio — recibe el link)"><input value={reqEmail} onChange={e => setReqEmail(e.target.value)} style={{ ...S.input, ...(reqEmail.trim() ? {} : { borderColor: 'var(--red-dim)' }) }} placeholder="tu@empresa.com" /></Field>
      </div>

      <div style={S.note}><KeyRound size={15} style={{ verticalAlign: -3 }} /> La contraseña temporal se envía como link de un solo uso al solicitante. El usuario la cambia en el primer inicio de sesión.</div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <button style={S.btn} disabled={busy} onClick={submit}><CheckCircle2 size={14} style={{ verticalAlign: -2 }} /> {busy ? 'Procesando…' : 'Crear usuario'}</button>
      </div>
    </div>
  );
}

function AccionesPanel({ flash }) {
  const S = styles;
  const [upn, setUpn] = useState('');
  const [reqEmail, setReqEmail] = useState('');
  const [off, setOff] = useState(null); // opciones de offboard
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok, title, text } — resultado persistente
  useEffect(() => { setResult(null); }, [upn]);
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!q.trim()) { setUsers([]); return; }
      setSearching(true);
      try { const r = await api.get(`/users?q=${encodeURIComponent(q.trim())}`); setUsers(Array.isArray(r) ? r : []); }
      catch { setUsers([]); }
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const reset = async () => {
    if (!upn.trim()) return flash('Ingresá el correo del usuario.', false);
    setBusy(true); setResult(null);
    const r = await api.post(`/users/${encodeURIComponent(upn.trim())}/reset-password`, { requester_email: reqEmail || null });
    const j = await r.json().catch(() => ({})); setBusy(false);
    if (r.ok && j.executed) { const t = `Contraseña de ${upn.trim()} reseteada. Se envió el link de un solo uso al solicitante.`; flash(t); setResult({ ok: true, title: 'Reset ejecutado', text: t }); }
    else if (r.status === 202) { const t = 'Solicitud de reset enviada para aprobación.'; flash(t); setResult({ ok: true, title: 'Enviado a aprobación', text: t }); }
    else { const t = j.error || 'Error reseteando.'; flash(t, false); setResult({ ok: false, title: 'Error', text: t }); }
  };
  const doOffboard = async (opts) => {
    setBusy(true); setResult(null);
    const r = await api.post(`/users/${encodeURIComponent(upn.trim())}/offboard`, { options: opts });
    const j = await r.json().catch(() => ({})); setBusy(false); setOff(null);
    if (r.ok && j.executed) { const t = `${upn.trim()} dado de baja: ${(j.steps || []).join(', ')}.`; flash(t); setResult({ ok: true, title: 'Baja ejecutada', text: t }); }
    else if (r.status === 202) { const t = 'Solicitud de baja enviada para aprobación.'; flash(t); setResult({ ok: true, title: 'Enviado a aprobación', text: t }); }
    else { const t = j.error || 'Error en la baja.'; flash(t, false); setResult({ ok: false, title: 'Error', text: t }); }
  };

  return (
    <div style={S.card}>
      <Field label="Buscar usuario"><input value={q} onChange={e => setQ(e.target.value)} style={S.input} placeholder="Nombre o correo…" /></Field>
      {searching && <div style={{ ...S.dim, marginTop: 6 }}>Buscando…</div>}
      {users.length > 0 && (
        <div style={{ display: 'grid', gap: 4, marginTop: 6, maxHeight: 240, overflowY: 'auto' }}>
          {users.map(u => (
            <div key={u.id} onClick={() => setUpn(u.upn)} style={{ ...S.optRow, cursor: 'pointer', ...(upn === u.upn ? { borderColor: 'var(--accent)', borderWidth: 2 } : {}) }}>
              <div>
                <div style={{ fontSize: 13.5 }}>{u.display_name || '(sin nombre)'}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink-2)' }}>{u.upn}</div>
              </div>
              <span style={{ marginLeft: 'auto' }}>{u.enabled ? <span style={S.chipOk}>activo</span> : <span style={S.chipNo}>deshabilitado</span>}</span>
            </div>
          ))}
        </div>
      )}
      {q.trim() && !searching && !users.length && <div style={{ ...S.dim, marginTop: 6 }}>Sin resultados.</div>}
      {upn && <div style={{ marginTop: 8, fontSize: 13 }}>Seleccionado: <b style={{ fontFamily: 'monospace' }}>{upn}</b></div>}
      <div style={{ marginTop: 10 }}><Field label="Mail del solicitante (para el reset)"><input value={reqEmail} onChange={e => setReqEmail(e.target.value)} style={S.input} placeholder="tu@empresa.com" /></Field></div>
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <button style={S.btn} disabled={busy || !upn.trim()} onClick={reset}><KeyRound size={14} style={{ verticalAlign: -2 }} /> Resetear contraseña</button>
        <button style={{ ...S.btn, background: 'var(--red)' }} disabled={busy || !upn.trim()} onClick={() => setOff({ revoke_sessions: true, remove_licenses: true })}><UserX size={14} style={{ verticalAlign: -2 }} /> Dar de baja…</button>
      </div>
      {result && (
        <div style={{ marginTop: 14, borderRadius: 8, padding: '12px 14px', border: '1px solid', borderColor: result.ok ? '#a7f3d0' : 'var(--red-dim)', background: result.ok ? 'var(--green-dim)' : 'var(--red-dim)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: result.ok ? 'var(--green)' : 'var(--red)' }}>
            {result.ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />} {result.title}
          </div>
          <div style={{ fontSize: 13.5, color: result.ok ? 'var(--green)' : 'var(--red)', marginTop: 4 }}>{result.text}</div>
        </div>
      )}
      {off && (
        <div style={S.modalWrap}>
          <div style={S.modal}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}><UserX size={16} style={{ verticalAlign: -3 }} /> Dar de baja</div>
            <div style={{ ...S.dim, marginBottom: 10 }}>{upn}</div>
            {[['Deshabilitar cuenta', 'disable', true, true], ['Revocar sesiones activas', 'revoke_sessions', off.revoke_sessions, false], ['Quitar licencias', 'remove_licenses', off.remove_licenses, false]].map(([label, key, val, locked]) => (
              <label key={key} style={S.offRow}>
                <span>{label}{locked && <span style={{ ...S.dim, marginLeft: 6 }}>obligatorio</span>}</span>
                <input type="checkbox" checked={val} disabled={locked} onChange={e => setOff(o => ({ ...o, [key]: e.target.checked }))} />
              </label>
            ))}
            <div style={{ ...S.warn, marginTop: 10 }}>Acción sensible: queda auditada con quién y cuándo.</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button style={S.btnGhost} onClick={() => setOff(null)}>Cancelar</button>
              <button style={{ ...S.btn, background: 'var(--red)' }} disabled={busy} onClick={() => doOffboard(off)}>Ejecutar baja</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SolicitudesPanel({ flash }) {
  const S = styles;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => { setLoading(true); api.get('/requests?status=pending').then(r => setRows(Array.isArray(r) ? r : [])).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, kind) => {
    const body = kind === 'reject' ? { reason: window.prompt('Motivo del rechazo (opcional):') || '' } : {};
    const r = await api.post(`/requests/${id}/${kind}`, body);
    const j = await r.json().catch(() => ({}));
    flash(r.ok ? (kind === 'approve' ? 'Aprobada y ejecutada.' : 'Rechazada.') : (j.error || j.result?.error || 'Error'), r.ok);
    load();
  };
  const label = { create: 'Alta', reset: 'Reset de contraseña', offboard: 'Baja' };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 600 }}>Solicitudes pendientes ({rows.length})</div>
        <button style={S.btnGhost} onClick={load}><RefreshCw size={13} style={{ verticalAlign: -2 }} /> Actualizar</button>
      </div>
      {loading && <div style={S.dim}>Cargando…</div>}
      {!loading && !rows.length && <div style={S.dim}>No hay solicitudes pendientes.</div>}
      <div style={{ display: 'grid', gap: 8 }}>
        {rows.map(r => (
          <div key={r.id} style={S.reqRow}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{label[r.action] || r.action} · <span style={{ fontFamily: 'monospace' }}>{r.target_upn || r.payload?.user_principal_name || '(alta)'}</span></div>
              <div style={S.dim}>Solicitante: {r.requested_by_name || r.requester_email || '—'} · {new Date(r.created_at).toLocaleString('es-AR')}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={S.btn} onClick={() => act(r.id, 'approve')}>Aprobar</button>
              <button style={S.btnGhost} onClick={() => act(r.id, 'reject')}>Rechazar</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (<div><div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 4 }}>{label}</div>{children}</div>);

const styles = {
  wrap: { maxWidth: 860, margin: '0 auto', padding: '4px 0' },
  head: { marginBottom: 14 },
  title: { fontSize: 20, fontWeight: 700, margin: 0, color: 'var(--ink)' },
  sub: { fontSize: 13, color: 'var(--ink-2)', marginTop: 2 },
  tabs: { display: 'flex', gap: 6, marginBottom: 14, borderBottom: '1px solid var(--border)' },
  tab: { font: 'inherit', fontSize: 13.5, background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '8px 12px', cursor: 'pointer', color: 'var(--ink-2)' },
  tabOn: { color: 'var(--ink)', borderBottomColor: 'var(--ink)', fontWeight: 600 },
  flash: { display: 'flex', alignItems: 'center', gap: 8, border: '1px solid', borderRadius: 8, padding: '9px 12px', fontSize: 13.5, marginBottom: 12, background: 'var(--surface)' },
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 },
  input: { width: '100%', font: 'inherit', fontSize: 14, border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', boxSizing: 'border-box' },
  optRow: { display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', cursor: 'pointer' },
  chipOk: { fontSize: 12, background: 'var(--green-dim)', color: 'var(--green)', borderRadius: 6, padding: '2px 8px' },
  chipNo: { fontSize: 12, background: 'var(--red-dim)', color: 'var(--red)', borderRadius: 6, padding: '2px 8px' },
  dim: { fontSize: 12.5, color: 'var(--ink-3)' },
  note: { display: 'flex', gap: 8, alignItems: 'center', background: 'var(--accent-dim)', color: '#1e40af', borderRadius: 8, padding: '9px 12px', fontSize: 12.5, marginTop: 12 },
  warn: { background: 'var(--amber-dim)', color: '#854d0e', borderRadius: 8, padding: '8px 12px', fontSize: 12.5 },
  btn: { font: 'inherit', fontSize: 13.5, fontWeight: 600, background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, padding: '9px 14px', cursor: 'pointer' },
  btnGhost: { font: 'inherit', fontSize: 13, fontWeight: 600, background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' },
  modalWrap: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { background: 'var(--surface)', borderRadius: 12, padding: '18px 20px', width: 420, maxWidth: '90vw' },
  offRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: 14 },
  reqRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' },
};
